import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import { useForm } from 'react-hook-form';
import ReactModal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useSystemMessage } from '../../../hooks/useSystemMessage';
import { taskApi } from '../../../apis/task/taskApi';
import icon05 from '../../../assets/images/ROOM_TaskFile_Re_05.png';
import {ApiClientError} from "../../../apis/errors";
import {ValidationErrorMessages} from "../common/ValidationErrorMessages";
import useAppRouter from "../../../hooks/useAppRouter";
import {logError} from "../../../libs/utils/logError";

interface CreateTaskModalProps {
  listId: number;
  onSubmitted: () => Promise<void>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}

interface FormValues {
  name: string;
}

export const CreateTaskModal = (props: CreateTaskModalProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    reValidateMode: 'onSubmit',
  });
  const { listId, onSubmitted, setShowModal } = props;
  const { success } = useSystemMessage();
  const location = useLocation();
  const { goto } = useAppRouter();

  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);

  const onSubmit = async (data: FormValues) => {
    setApiClientError(null);
    try {
      await taskApi.create({ taskName: data.name, listId }, {});
      await onSubmitted();
      await success('タスクを作成しました。');
      setShowModal(false);
    } catch(e) {
      await logError(
        location.pathname,
        'タスクの作成に失敗しました。',
        e
      );

      if (e instanceof ApiClientError) {
        setApiClientError(e);
        return;
      }

      goto('internalServerError');
    }
  };

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => setShowModal(false)}
        className="content"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">新規タスク追加</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowModal(false)}
            >
              <img src={icon05} alt="icon" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-modal-content">
              <div className="text-danger text-xs mb-4">
                { apiClientError?.message }
                { (Object.keys(errors).length > 0) && '入力エラーがあります' }
              </div>
              <input
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...register('name', { required: true, maxLength: 100 })}
                type="text"
                placeholder="新規タスクを入力してください"
                className="text-field"
              />
              {errors.name && errors.name.type === 'required' && (
                <span className="my-2 text-red-500 text-xs">
                  タスク名は必須です
                </span>
              )}
              {errors.name && errors.name.type === 'maxLength' && (
                <span className="my-2 text-red-500 text-xs">
                  タスク名は100文字以下で指定してください
                </span>
              )}
              <ValidationErrorMessages
                {...{
                  fieldErrors: apiClientError?.errors,
                  field: 'taskName',
                }}
              />
            </div>
            <div className="form-modal-footer-container">
              <div className="form-modal-footer">
                <button
                  className="btn-sub"
                  type="button"
                  onClick={() => setShowModal(false)}
                >
                  キャンセル
                </button>
                <button
                  className="btn-primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin mr-4"
                      />
                      保 存
                    </>
                  ) : (
                    '保　存'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </ReactModal>
    </>
  );
};
